import { useEffect, useRef } from 'react';
import type { WellborePointsResponse } from 'types/api-responses';

// How long, and how often, to re-read the points while a derive is awaited.
const GEOMETRY_POLL_MS = 3_000;
const GEOMETRY_POLL_ATTEMPTS = 10;

interface UseGeometryDeriveWaitArgs {
  /** A geometry_construction push is awaited. */
  pending: boolean;
  pointsHref: string | undefined;
  pointsData: WellborePointsResponse | null;
  refetchPoints: () => void;
  /** The derive finished, exactly as its push would report. */
  onProcessed: () => void;
  onFailed: () => void;
  /** Nothing moved by the last read: stop waiting so the controls come back. */
  onExhausted: () => void;
}

/**
 * Backs the derive's push with a bounded re-read of the points, since a dropped push
 * and a run the backend declines both leave the status unchanged with nothing to wait
 * on. Whichever of push or re-read lands first settles the wait.
 * @param {UseGeometryDeriveWaitArgs} args The wait, the points read and its outcomes.
 */
export const useGeometryDeriveWait = ({
  pending,
  pointsHref,
  pointsData,
  refetchPoints,
  onProcessed,
  onFailed,
  onExhausted,
}: UseGeometryDeriveWaitArgs): void => {
  // The payload on screen when the wait was armed predates the enqueue, so settling
  // on it would end the wait on a healthy bore's old `processed`.
  const latestPointsRef = useRef(pointsData);
  latestPointsRef.current = pointsData;
  const armedPointsRef = useRef<WellborePointsResponse | null>(null);
  // Settling re-reads the points itself, so without this the same wait would settle
  // again on its own re-read before the pending flag clears.
  const settledRef = useRef(false);
  useEffect(() => {
    if (!pending || !pointsHref) return undefined;
    armedPointsRef.current = latestPointsRef.current;
    settledRef.current = false;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      refetchPoints();
      if (attempts >= GEOMETRY_POLL_ATTEMPTS) {
        window.clearInterval(id);
        onExhausted();
      }
    }, GEOMETRY_POLL_MS);
    return () => window.clearInterval(id);
    // `onExhausted` must be stable; the interval restarts only on a new wait.
  }, [pending, pointsHref, refetchPoints, onExhausted]);
  useEffect(() => {
    if (!pending || !pointsData) return;
    if (settledRef.current || pointsData === armedPointsRef.current) return;
    if (pointsData.geometryStatus === 'processed') {
      settledRef.current = true;
      onProcessed();
    } else if (pointsData.geometryStatus === 'failed') {
      settledRef.current = true;
      onFailed();
    }
  }, [pending, pointsData, onProcessed, onFailed]);
};
